package com.eladfinish.bible.contest.screens.verseWidget

import com.eladfinish.bible.contest.analytics.AnalyticsConstants
import com.eladfinish.bible.contest.analytics.providers.AnalyticsProvider
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.features.verseWidget.GetVerseWidgetExampleImageUrlUseCase
import com.eladfinish.bible.contest.features.verseWidget.GetWidgetVerseUseCase
import com.eladfinish.bible.contest.features.verseWidget.UpdateVerseWidgetUserPropertyUseCase
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetAnalytics
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetConfig
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetConfigRepository
import com.eladfinish.bible.contest.features.verseWidget.VerseWidgetHost
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.MutableStateFlow
import kotlin.time.Clock

/**
 * The settings of the home-screen widget. Every change is saved at once and handed to the widget through
 * [VerseWidgetHost.onConfigChanged]; the preview shows the verse the widget shows now with those settings.
 */
class VerseWidgetStore(
    private val configRepository: VerseWidgetConfigRepository,
    private val getWidgetVerseUseCase: GetWidgetVerseUseCase,
    private val host: VerseWidgetHost,
    private val analyticsProvider: AnalyticsProvider,
    private val updateVerseWidgetUserProperty: UpdateVerseWidgetUserPropertyUseCase,
    private val getExampleImageUrl: GetVerseWidgetExampleImageUrlUseCase,
) : ViewModelStore<VerseWidgetState, VerseWidgetAction, VerseWidgetEffect>(tag = "VerseWidgetStore") {

    override val stateFlow = MutableStateFlow(
        configRepository.get().let { config ->
            VerseWidgetState(
                config = config,
                preview = null,
                installedCount = host.installedCount(),
                canRequestPin = host.canRequestPin,
            )
        }
    )

    override fun handleAction(action: VerseWidgetAction): VerseWidgetState =
        when (action) {
            is VerseWidgetAction.Opened -> handleOpened(action)
            VerseWidgetAction.Resumed -> handleResumed()
            is VerseWidgetAction.SourceSelected -> change(
                field = AnalyticsConstants.VerseWidget.FIELD_SOURCE,
                value = action.source.name.lowercase(),
            ) { it.copy(source = action.source) }

            is VerseWidgetAction.BookSelected -> change(
                field = AnalyticsConstants.VerseWidget.FIELD_BOOK,
                value = action.bookNumber.toString(),
            ) { it.copy(bookNumber = action.bookNumber) }

            is VerseWidgetAction.OrderSelected -> change(
                field = AnalyticsConstants.VerseWidget.FIELD_ORDER,
                value = action.order.name.lowercase(),
            ) { it.copy(order = action.order) }

            is VerseWidgetAction.IntervalSelected -> change(
                field = AnalyticsConstants.VerseWidget.FIELD_INTERVAL,
                value = action.minutes.toString(),
            ) { it.copy(intervalMinutes = action.minutes) }

            is VerseWidgetAction.NikudChanged -> change(
                field = AnalyticsConstants.VerseWidget.FIELD_NIKUD,
                value = action.showNikud.toString(),
            ) { it.copy(showNikud = action.showNikud) }

            is VerseWidgetAction.TeamimChanged -> change(
                field = AnalyticsConstants.VerseWidget.FIELD_TEAMIM,
                value = action.showTeamim.toString(),
            ) { it.copy(showTeamim = action.showTeamim) }

            VerseWidgetAction.AddClicked -> handleAddClicked()
            VerseWidgetAction.InstructionsDismissed -> currentState.copy(isInstructionsVisible = false)
            VerseWidgetAction.ExampleClicked -> handleExampleClicked()
            VerseWidgetAction.ExampleDismissed -> currentState.copy(isExampleVisible = false)
        }

    private fun handleOpened(action: VerseWidgetAction.Opened): VerseWidgetState {
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_SCREEN_OPENED,
            // `source` is the way in, so the content source goes under another name
            VerseWidgetAnalytics.params(currentState.config, sourceKey = AnalyticsConstants.VerseWidget.PARAM_VERSE_SOURCE) +
                    (AnalyticsConstants.VerseWidget.PARAM_SOURCE to action.entryPoint.analyticsValue),
        )
        return currentState
    }

    private fun handleResumed(): VerseWidgetState {
        host.onSettingsShown()
        updateVerseWidgetUserProperty()
        return currentState.copy(
            preview = previewOf(currentState.config),
            installedCount = host.installedCount(),
            canRequestPin = host.canRequestPin,
        )
    }

    private fun handleExampleClicked(): VerseWidgetState {
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_EXAMPLE_OPENED,
            VerseWidgetAnalytics.params(currentState.config),
        )
        return currentState.copy(isExampleVisible = true, exampleImageUrl = getExampleImageUrl())
    }

    private fun handleAddClicked(): VerseWidgetState {
        if (host.canRequestPin) {
            analyticsProvider.logEvent(
                AnalyticsConstants.VerseWidget.EVENT_PIN_REQUESTED,
                VerseWidgetAnalytics.params(currentState.config),
            )
            // The system shows its own dialog; when it cannot, the user is told how to do it by hand.
            if (host.requestPin()) return currentState
        }
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_INSTRUCTIONS_VIEWED,
            VerseWidgetAnalytics.params(currentState.config),
        )
        return currentState.copy(isInstructionsVisible = true)
    }

    private fun change(field: String, value: String, edit: (VerseWidgetConfig) -> VerseWidgetConfig): VerseWidgetState {
        val before = currentState.config
        if (edit(before) == before) return currentState
        val config = configRepository.update(edit)
        host.onConfigChanged(config)
        analyticsProvider.logEvent(
            AnalyticsConstants.VerseWidget.EVENT_CONFIG_CHANGED,
            // What was changed, and all the settings as they are after the change
            VerseWidgetAnalytics.params(config) + mapOf(
                AnalyticsConstants.VerseWidget.PARAM_FIELD to field,
                AnalyticsConstants.VerseWidget.PARAM_VALUE to value,
            ),
        )
        updateVerseWidgetUserProperty()
        return currentState.copy(config = config, preview = previewOf(config))
    }

    private fun previewOf(config: VerseWidgetConfig) =
        runCatching { getWidgetVerseUseCase(config = config, instant = Clock.System.now()) }
            .onFailure { Napier.e("verse widget preview failed", it, tag = "VerseWidgetStore") }
            .getOrNull()
}
